import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Small, unobtrusive version indicator.
 * Fetches GET /api/system/version and renders "v<version> · <env>".
 * Silent on any error (renders null).
 *
 * Props:
 *   className?: string  - extra Tailwind classes
 *   theme?: 'light' | 'dark'
 */
export default function VersionBadge({ className = '', theme = 'light' }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/system/version`, { timeout: 5000 })
      .then((res) => {
        if (mounted) setInfo(res.data);
      })
      .catch(() => {
        // Silent fail – do not disturb UI
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!info?.version) return null;

  const envLabel = {
    preview: 'Preview',
    production: 'Live',
    development: 'Dev',
  }[info.environment] || info.environment;

  const envColor = {
    preview: 'bg-amber-100 text-amber-800 border-amber-200',
    production: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    development: 'bg-slate-100 text-slate-700 border-slate-200',
  }[info.environment] || 'bg-slate-100 text-slate-700 border-slate-200';

  const textColor = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';

  return (
    <div
      data-testid="version-badge"
      className={`flex items-center gap-1.5 text-[10px] leading-none ${textColor} ${className}`}
      title={`Version ${info.version} (${envLabel})`}
    >
      <span className="font-mono">v{info.version}</span>
      <span
        className={`inline-block px-1.5 py-0.5 rounded border ${envColor} font-medium`}
        data-testid="version-badge-env"
      >
        {envLabel}
      </span>
    </div>
  );
}
